import {Injectable} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';

export interface PageSeoMeta {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
}

export interface ListingSeoInput {
  name: string;
  description?: string | null;
  price?: number | null;
  currency?: string;
  status?: string | null;
  imageUrls?: string[];
  pageUrl?: string;
  sellerName?: string | null;
  brand?: string | null;
  attributes?: Array<{label?: string; displayValue?: string; code?: string}>;
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly jsonLdListingId = 'listing-jsonld';
  private readonly jsonLdBrowseId = 'browse-jsonld';

  constructor(private title: Title, private meta: Meta) {}

  /** Resolve a possibly relative URL against the current origin. */
  absoluteUrl(url: string | null | undefined): string {
    if (!url) {
      return typeof window !== 'undefined' ? window.location.href.split('?')[0] : '';
    }
    const trimmed = url.trim();
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }
    if (typeof window === 'undefined') {
      return trimmed;
    }
    if (trimmed.startsWith('//')) {
      return `${window.location.protocol}${trimmed}`;
    }
    if (trimmed.startsWith('/')) {
      return `${window.location.origin}${trimmed}`;
    }
    return `${window.location.origin}/${trimmed}`;
  }

  absoluteImageUrl(imagesBase: string | null | undefined, pathOrUrl: string | null | undefined): string {
    if (!pathOrUrl) {
      return '';
    }
    if (/^https?:\/\//i.test(pathOrUrl) || pathOrUrl.startsWith('//')) {
      return this.absoluteUrl(pathOrUrl);
    }
    const base = (imagesBase || '').replace(/\/+$/, '');
    const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    if (/^https?:\/\//i.test(base)) {
      return `${base}${path}`;
    }
    return this.absoluteUrl(`${base}${path}`);
  }

  updatePage(meta: PageSeoMeta): void {
    if (meta.title) {
      this.title.setTitle(meta.title);
      this.meta.updateTag({name: 'title', content: meta.title});
      this.meta.updateTag({property: 'og:title', content: meta.title});
      this.meta.updateTag({name: 'twitter:title', content: meta.title});
    }
    if (meta.description) {
      this.meta.updateTag({name: 'description', content: meta.description});
      this.meta.updateTag({property: 'og:description', content: meta.description});
      this.meta.updateTag({name: 'twitter:description', content: meta.description});
    }
    if (meta.image) {
      const image = this.absoluteUrl(meta.image);
      this.meta.updateTag({property: 'og:image', content: image});
      this.meta.updateTag({name: 'twitter:image', content: image});
      this.meta.updateTag({name: 'twitter:card', content: 'summary_large_image'});
    }
    if (meta.url) {
      const url = this.absoluteUrl(meta.url);
      this.meta.updateTag({property: 'og:url', content: url});
      this.setCanonical(url);
    }
    if (meta.type) {
      this.meta.updateTag({property: 'og:type', content: meta.type});
    }
  }

  setCanonical(url: string): void {
    let link: HTMLLinkElement | null = document.querySelector('link[rel=\'canonical\']');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', this.absoluteUrl(url));
  }

  setJsonLd(id: string, data: object): void {
    this.removeJsonLd(id);
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
  }

  removeJsonLd(id: string): void {
    const existing = document.getElementById(id);
    if (existing) {
      existing.remove();
    }
  }

  applyListingProduct(input: ListingSeoInput): void {
    const name = (input.name || '').trim();
    if (!name) {
      return;
    }
    const description = this.truncate(
      (input.description || '').trim() || `View ${name} on our marketplace.`,
      160
    );
    const pageUrl = this.absoluteUrl(input.pageUrl || (typeof window !== 'undefined' ? window.location.href.split('?')[0] : ''));
    const images = (input.imageUrls || []).filter(Boolean).map(u => this.absoluteUrl(u));
    const currency = input.currency || 'EUR';
    const availability = this.offerAvailability(input.status);

    this.updatePage({
      title: `${name} | Marketplace`,
      description,
      image: images[0],
      url: pageUrl,
      type: 'product'
    });

    const additionalProperty = (input.attributes || [])
      .filter(a => (a.displayValue || '').trim())
      .slice(0, 20)
      .map(a => ({
        '@type': 'PropertyValue',
        name: a.label || a.code || 'Attribute',
        value: a.displayValue
      }));

    const jsonLd: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name,
      description: (input.description || name).trim(),
      image: images,
      offers: {
        '@type': 'Offer',
        priceCurrency: currency,
        price: input.price ?? 0,
        availability,
        url: pageUrl
      }
    };
    if (input.brand || input.sellerName) {
      jsonLd.brand = {
        '@type': 'Brand',
        name: input.brand || input.sellerName
      };
    }
    if (input.sellerName) {
      jsonLd.seller = {
        '@type': 'Organization',
        name: input.sellerName
      };
    }
    if (additionalProperty.length) {
      jsonLd.additionalProperty = additionalProperty;
    }
    this.setJsonLd(this.jsonLdListingId, jsonLd);
  }

  clearListingProduct(): void {
    this.removeJsonLd(this.jsonLdListingId);
  }

  applyBrowsePage(opts: {
    title: string;
    description?: string;
    pageUrl?: string;
    breadcrumbs?: Array<{name: string; url: string}>;
  }): void {
    const pageUrl = this.absoluteUrl(opts.pageUrl || (typeof window !== 'undefined' ? window.location.href.split('?')[0] : ''));
    const description = this.truncate(
      opts.description || `Browse ${opts.title} listings on our marketplace.`,
      160
    );
    this.updatePage({
      title: opts.title,
      description,
      url: pageUrl,
      type: 'website'
    });

    const crumbs = opts.breadcrumbs || [];
    if (crumbs.length) {
      this.setJsonLd(this.jsonLdBrowseId, {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: opts.title,
        description,
        url: pageUrl,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: crumbs.map((c, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: c.name,
            item: this.absoluteUrl(c.url)
          }))
        }
      });
    } else {
      this.setJsonLd(this.jsonLdBrowseId, {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: opts.title,
        description,
        url: pageUrl
      });
    }
  }

  clearBrowsePage(): void {
    this.removeJsonLd(this.jsonLdBrowseId);
  }

  private offerAvailability(status?: string | null): string {
    const s = (status || 'PUBLISHED').toUpperCase();
    if (s === 'PUBLISHED' || s === 'LIVE') {
      return 'https://schema.org/InStock';
    }
    if (s === 'SOLD' || s === 'EXPIRED') {
      return 'https://schema.org/SoldOut';
    }
    return 'https://schema.org/OutOfStock';
  }

  private truncate(value: string, max: number): string {
    if (value.length <= max) {
      return value;
    }
    return value.slice(0, max - 1).trimEnd() + '…';
  }
}
